<template>
  <v-card style="width: 100%" class="mx-2 mb-4">
    <v-card-text>
      <v-form ref="form">
        <v-row no-gutters>
          <v-text-field
            :label="$t('mentorship.actions.edit.title')"
            v-model="a.title"
            :rules="[rules.required]"
          >
            <template v-slot:prepend
              ><span class="title"> {{ actionIndex + 1 }}.</span>
            </template>
          </v-text-field>
          <v-btn color="primary" icon @click="save">
            <v-icon>save</v-icon>
          </v-btn>
          <v-btn color="grey darken-1" icon @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </v-row>

        <v-row class="ma-0" justify="space-between" v-if="editor">
          <v-item-group class="v-btn-toggle">
            <v-btn
              @click="toggleItalic"
              :class="{ 'v-btn--active': editor.isActive('italic') }"
            >
              <v-icon>format_italic</v-icon>
            </v-btn>

            <v-btn
              @click="toggleBold"
              :class="{ 'v-btn--active': editor.isActive('bold') }"
            >
              <v-icon>format_bold</v-icon>
            </v-btn>

            <v-btn
              @click="toggleUnderline"
              :class="{ 'v-btn--active': editor.isActive('underline') }"
            >
              <v-icon>format_underline</v-icon>
            </v-btn>
          </v-item-group>

          <v-item-group class="v-btn-toggle">
            <color-btn
              :isActive="editor.isActive('textStyle', { color: '#000000' })"
              color="#000000"
              @click="setColor('#000000')"
            />
            <color-btn
              :isActive="editor.isActive('textStyle', { color: '#F44336' })"
              color="#F44336"
              @click="setColor('#F44336')"
            />
            <color-btn
              :isActive="editor.isActive('textStyle', { color: '#4CAF50' })"
              color="#4CAF50"
              @click="setColor('#4CAF50')"
            />
          </v-item-group>

          <v-item-group class="v-btn-toggle">
            <v-btn
              @click="toggleBulletList"
              :class="{ 'v-btn--active': editor.isActive('bulletList') }"
            >
              <v-icon>format_list_bulleted</v-icon>
            </v-btn>
            <v-btn
              @click="toggleOrderedList"
              :class="{ 'v-btn--active': editor.isActive('orderedList') }"
            >
              <v-icon>format_list_numbered</v-icon>
            </v-btn>
          </v-item-group>

          <v-item-group class="v-btn-toggle">
            <v-btn @click="toggleLink">
              <v-icon>link</v-icon>
            </v-btn>
          </v-item-group>
        </v-row>
        <editor-content
          :class="{ 'is-invalid': !isContentValid }"
          class="mt-4"
          :editor="editor"
        />
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextStyle from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import ColorBtn from "./control/ColorBtn.vue";
export default {
  props: {
    actionIndex: {
      type: Number,
      required: true,
    },
    action: {
      type: Object,
      required: true,
    },
  },
  components: {
    EditorContent,
    ColorBtn,
  },
  data() {
    return {
      a: {
        title: "",
        content: "",
      },
      isValidated: false,
      editor: null,
      rules: {
        required: (value) => {
          const isValid = !!value;
          return isValid || this.$i18n.t("validation.required");
        },
      },
    };
  },
  mounted() {
    this.a = Object.assign({}, this.action);
    this.editor = new Editor({
      content: this.a.content,
      extensions: [StarterKit, Underline, TextStyle, Color, Link],
    });
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  },

  computed: {
    isContentValid() {
      if (!this.isValidated) {
        return true;
      }
      if (!this.editor) {
        return false;
      }
      const content = this.editor.getHTML();
      if (content == "<p></p>") {
        return false;
      }

      return true;
    },
  },
  methods: {
    toggleLink() {
      const selectedText = this.editor.state.selection.$from.parent.textContent;
      if (!selectedText) {
        return;
      }

      this.editor
        .chain()
        .focus()
        .toggleLink({ href: selectedText, target: "_blank" })
        .run();
    },
    toggleUnderline() {
      this.editor
        .chain()
        .focus()
        .toggleUnderline()
        .run();
    },
    toggleBold() {
      this.editor
        .chain()
        .focus()
        .toggleBold()
        .run();
    },
    toggleItalic() {
      this.editor
        .chain()
        .focus()
        .toggleItalic()
        .run();
    },
    toggleBulletList() {
      this.editor
        .chain()
        .focus()
        .toggleBulletList()
        .run();
    },
    toggleOrderedList() {
      this.editor
        .chain()
        .focus()
        .toggleOrderedList()
        .run();
    },
    setColor(color) {
      this.editor
        .chain()
        .focus()
        .setColor(color)
        .run();
    },

    close() {
      const isValid = this.$refs.form.validate();
      this.$emit("isValidChange", isValid);

      this.$emit("close");
    },
    validateForm() {
      const isValid = this.$refs.form.validate();
      this.isValidated = true;
      return isValid;
    },
    save() {
      this.a.content = this.editor.getHTML();
      const isFormValid = this.validateForm();

      const isValid = isFormValid && this.isContentValid;
      if (isValid) {
        this.$emit("isValidChange", isValid);
        this.$emit("save", this.a);
      }
    },
  },
};
</script>

<style>
/* Basic editor styles */
.ProseMirror {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  outline: none;
}
.is-invalid .ProseMirror {
  border-color: red;
  animation: blink 0.5s 2;
}
@keyframes blink {
  0%,
  100% {
    border-color: red;
  }
  50% {
    border-color: transparent;
  }
}
</style>
